import React from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import Button from 'components/Button/Button';
import ContentLoader from 'components/ContentLoader/ContentLoader';
import EmptyTaskManagement from 'components/EmptyTaskManagement/EmptyTaskManagement';
import { HEAD_IRO_MAIN_EMAILS_CELLS, HEAD_IRO_SEND_LOCAL_EMAILS_CELLS, TASK_HEAD_IRO_MAIN_CELLS } from 'constants/dataSets';
import { MODALS_IRO_IDENTIFICATION, TABS_IRO_IDENTIFICATION } from 'constants/general';
import { BUTTON_VARIANT } from 'constants/interfaces';
import DeleteModal from 'pages/CompanyAdminPages/ValueChainMap/components/DeleteModal/DeleteModal';

import AddUserModal from './components/AddUserModal/AddUserModal';
import CreateCustomIROItem from './components/CreateCustomIROItem/CreateCustomIROItem';
import ItemsTableMain from './components/ItemsTableMain/ItemsTableMain';
import ItemsTableMainEmails from './components/ItemsTableMainEmails/ItemsTableMainEmails';
import RespondentTable from './components/RespondentTable/RespondentTable';
import SelectedItemModal from './components/SelectedItemModal/SelectedItemModal';
import SendQuestionary from './components/SendQuestionary/SendQuestionary';

import type IMaterialAssessmentIROIndetificator from './interfaces/IMaterialAssessmentIROIndetificator';

import styles from './scss/MaterialAssessmentIROIndetificator.module.scss';

function MaterialAssessmentIROIndetificatorView({
  items,
  control,
  openModal,
  activeTab,
  handleGoBack,
  handleClickTab,
  handleAddSubmit,
  handleCloseModal,
  handleAddNewImpact,
  handleCreateCustomItem,
  hasMore,
  loading,
  onLoadMore,
  naceCodeTreeData,
  onSelectItem,
  selectedItems,
  handleUnSelectAll,
  standards,
  activeStandard,
  handleActiveStandard,
  hasMoreMain,
  onLoadMoreMain,
  itemsMain,
  itemsEmailsList,
  createIROItem,
  deleteTemplate,
  isDeleteModalOpen,
  onDeleteConfirm,
  handleEdit,
  selectEditItem,
  updateIROItem,
  handleNext,
  handleResend,
  handleCancel,
  mainVcTabs,
  handleClickVcTab,
  activeVcTab,
  respondentList,
  onLoadMoreRespondent,
  hasMoreRespondent,
  handleChangeRespondentListKeyword,
  respondentListKeyword,
  handleAddEmail,
  handleSendEmails,
  companyId,
  selectedRespondentItems,
  handleSelectRespondentItem,
  handleSendQuestionary,
  handleSelectTemplateItem,
  handleCreateRecipientUsers,
  isLoadingRecipientList,
  isViewOnly,
}: IMaterialAssessmentIROIndetificator) {
  const { t } = useTranslation();

  const checkEmailList = itemsEmailsList?.length > 0;

  return (
    <div className={styles.root}>
      <div className={styles.rootContainer}>
        <div className={styles.wrapper}>
          <div className={styles.tabs}>
            {mainVcTabs.map((item) => (
              <Button
                key={[item.id, item.name].join('-')}
                title={item.name}
                variant={BUTTON_VARIANT.ghost}
                onClick={() => handleClickVcTab(item.id)}
                className={clsx(styles.tab, { [styles.active]: activeVcTab === item.id })}
              />
            ))}
          </div>
          <div className={styles.titleRow}>
            <span className={styles.titleHint}>{t('materialityAssessment.titleIROIdentification')}</span>
            {activeTab !== 4 && activeTab !== 5 && !isViewOnly && (
              <Button
                className={styles.button}
                title={t(
                  'materialityAssessment.addNewImpact',
                  { name: TABS_IRO_IDENTIFICATION.find((tab) => tab.id === activeTab)?.name },
                )}
                onClick={handleAddNewImpact}
              />
            )}
          </div>
          <div className={styles.contentWrapper}>
            <div className={styles.tabs}>
              {TABS_IRO_IDENTIFICATION.filter((tab) => !isViewOnly || tab.id !== 5).map((item) => (
                <Button
                  key={[item.id, item.name].join('-')}
                  title={t(`materialityAssessment.${item.value}`)}
                  variant={BUTTON_VARIANT.ghost}
                  onClick={() => handleClickTab(item.id)}
                  className={clsx(styles.tab, { [styles.active]: activeTab === item.id })}
                />
              ))}
            </div>
            <div className={styles.content}>
              {loading ? (<ContentLoader title={t('materialityAssessment.loadingIRO')} />) : null}
              {(itemsMain.length === 0 && activeTab !== 4) && !loading && activeTab !== 5 ? (
                <EmptyTaskManagement
                  title={t(
                    'materialityAssessment.emptyIRODescription',
                    { name: TABS_IRO_IDENTIFICATION.find((tab) => tab.id === activeTab)?.name },
                  )}
                  buttonTitle={t(
                    'materialityAssessment.addNewImpact',
                    { name: TABS_IRO_IDENTIFICATION.find((tab) => tab.id === activeTab)?.name },
                  )}
                  buttonOnClick={handleAddNewImpact}
                  checkIcon
                  hideButton={(!checkEmailList && activeTab === 4) || isViewOnly}
                />
              ) : null}
              {itemsEmailsList.length === 0 && activeTab === 4 && !loading ? (
                <EmptyTaskManagement
                  title={t('materialityAssessment.emptySentEmails')}
                  checkIcon
                />
              ) : null}
              {(itemsMain.length > 0 || itemsEmailsList.length > 0 || activeTab === 5) && !loading && (
                <div className={styles.tableWrapper}>
                  {activeTab !== 4 && activeTab !== 5 && itemsMain.length > 0 ? (
                    <ItemsTableMain
                      items={itemsMain}
                      headCells={TASK_HEAD_IRO_MAIN_CELLS}
                      onLoadMore={onLoadMoreMain}
                      hasMore={hasMoreMain}
                      isLoadingMore={loading}
                      naceCodeTreeData={naceCodeTreeData}
                      deleteTemplate={deleteTemplate}
                      handleEdit={handleEdit}
                      isInfoTable
                      disableActions={isViewOnly}
                    />
                  ) : null}
                  {activeTab === 4 && itemsEmailsList.length > 0 ? (
                    <ItemsTableMainEmails
                      items={itemsEmailsList}
                      headCells={HEAD_IRO_MAIN_EMAILS_CELLS}
                      onLoadMore={onLoadMoreMain}
                      hasMore={hasMoreMain}
                      isLoadingMore={loading}
                      handleResend={handleResend}
                      disableActions={isViewOnly}
                    />
                  ) : null}
                  {activeTab === 5 ? (
                    <RespondentTable
                      items={respondentList}
                      headCells={HEAD_IRO_SEND_LOCAL_EMAILS_CELLS}
                      onLoadMore={onLoadMoreRespondent}
                      hasMore={hasMoreRespondent}
                      isLoadingMore={loading}
                      handleChangeRespondentListKeyword={handleChangeRespondentListKeyword}
                      respondentListKeyword={respondentListKeyword}
                      handleAddEmail={handleAddEmail}
                      handleSendEmails={handleSendEmails}
                      selectedRespondentItems={selectedRespondentItems}
                      handleSelectRespondentItem={handleSelectRespondentItem}
                      isInfoTable
                      showHeaderCheckbox
                      isLoadingRecipientList={isLoadingRecipientList}
                      disableActions={isViewOnly}
                    />
                  ) : null}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className={styles.buttonWrapper}>
          <Button
            title={t('common.cancel')}
            variant={BUTTON_VARIANT.cancel}
            className={styles.button}
            onClick={handleCancel}
          />
          <div className={styles.rightButtons}>
            <Button
              title={t('common.prev')}
              onClick={handleGoBack}
              variant={BUTTON_VARIANT.cancel}
              className={styles.prevButton}
            />
            <Button
              title={t('common.next')}
              onClick={handleNext}
              className={styles.button}
            />
          </div>
        </div>
        <SelectedItemModal
          open={openModal === MODALS_IRO_IDENTIFICATION.ADD_NEW_ITEM}
          title={t('materialityAssessment.selectImpacts', {
            name: TABS_IRO_IDENTIFICATION.find((tab) => tab.id === activeTab)?.name,
          })}
          titleButton={t('materialityAssessment.addCustomImpact', {
            name: TABS_IRO_IDENTIFICATION.find((tab) => tab.id === activeTab)?.name,
          })}
          tabName={TABS_IRO_IDENTIFICATION.find((tab) => tab.id === activeTab)?.name || ''}
          loading={false}
          handleCancel={handleCloseModal}
          handleAddSubmit={handleAddSubmit}
          handleCreateCustomItem={handleCreateCustomItem}
          control={control}
          items={items}
          hasMore={hasMore}
          onLoadMore={onLoadMore}
          loadingItems={loading}
          naceCodeTreeData={naceCodeTreeData}
          onSelectItem={onSelectItem}
          selectedItems={selectedItems}
          handleUnSelectAll={handleUnSelectAll}
          standards={standards}
          activeStandard={activeStandard}
          handleActiveStandard={handleActiveStandard}
        />
        <CreateCustomIROItem
          open={openModal === MODALS_IRO_IDENTIFICATION.ADD_IMPACT
            || openModal === MODALS_IRO_IDENTIFICATION.ADD_RISKS
            || openModal === MODALS_IRO_IDENTIFICATION.ADD_OPPORTUNITY}
          handleCancel={handleCloseModal}
          createIROItem={createIROItem}
          title={selectEditItem ? t(
            'materialityAssessment.editTemplate',
            { name: openModal },
          ) : t(
            'materialityAssessment.addCustomTemplate',
            { name: openModal },
          )}
          standards={standards}
          selectEditItem={selectEditItem}
          naceCodeTreeData={naceCodeTreeData}
          updateIROItem={updateIROItem}
          opportunity={activeTab === 3}
        />
        <DeleteModal
          open={isDeleteModalOpen}
          onReject={handleCloseModal}
          isLoading={false}
          name=""
          title={TABS_IRO_IDENTIFICATION.find((tab) => tab.id === activeTab)?.name}
          onConfirm={onDeleteConfirm}
        />
        <AddUserModal
          open={openModal === MODALS_IRO_IDENTIFICATION.ADD_NEW_EMAIL}
          onClose={handleCloseModal}
          title={t('materialityAssessment.addEmailButton')}
          companyId={companyId}
          handleCreateRecipientUsers={handleCreateRecipientUsers}
        />
        <SendQuestionary
          open={openModal === MODALS_IRO_IDENTIFICATION.SEND_EMAILS}
          title={t('materialityAssessment.sendQuestionary')}
          loading={loading}
          itemsMain={itemsMain}
          activeTab={activeTab}
          handleClickTab={handleClickTab}
          handleCancel={handleCloseModal}
          handleAddSubmit={handleSendQuestionary}
          naceCodeTreeData={naceCodeTreeData}
          onLoadMore={onLoadMoreMain}
          hasMore={hasMoreMain}
          loadingItems={loading}
          control={control}
          onSelectItem={onSelectItem}
          selectedItems={selectedItems}
          handleUnSelectAll={handleUnSelectAll}
          handleSelectTemplateItem={handleSelectTemplateItem}
        />
      </div>
    </div>
  );
}

export default MaterialAssessmentIROIndetificatorView;
